import { Component, Fragment, type ErrorInfo, type ReactNode } from "react"

export interface RenderErrorBoundaryFallbackContext {
  readonly error: Error
  readonly retry: () => void
}

export interface RenderErrorBoundaryProps {
  readonly children: ReactNode
  readonly fallback: (context: RenderErrorBoundaryFallbackContext) => ReactNode
  readonly onError?: (error: Error, info: ErrorInfo) => void
}

interface RenderErrorBoundaryState {
  readonly error: Error | null
  readonly generation: number
}

/**
 * Catches unexpected React render/lifecycle errors in its descendant tree.
 * Expected operation failures should remain in their owning operation state.
 */
export class RenderErrorBoundary extends Component<RenderErrorBoundaryProps, RenderErrorBoundaryState> {
  state: RenderErrorBoundaryState = {
    error: null,
    generation: 0,
  }

  static getDerivedStateFromError(error: Error): Partial<RenderErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info)
  }

  private readonly retry = () => {
    this.setState((state) => ({
      error: null,
      generation: state.generation + 1,
    }))
  }

  render() {
    if (this.state.error != null) {
      return this.props.fallback({
        error: this.state.error,
        retry: this.retry,
      })
    }

    return <Fragment key={this.state.generation}>{this.props.children}</Fragment>
  }
}
